import { Button, cn } from "@cloudflare/kumo";
import {
  useEffect,
  useId,
  useState,
  type ChangeEvent,
  type SyntheticEvent
} from "react";
import { validateKey } from "@workerstation/shared";

// Adapted from workers-sdk's local-explorer-ui AddKVForm.
interface AddKVFormProps {
  onAdd: (key: string, value: string) => void;
  /** Increment to clear the form from the outside (e.g. after overwrite). */
  clearSignal?: number;
  disabled?: boolean;
  saving?: boolean;
}

export function AddKVForm({
  onAdd,
  clearSignal = 0,
  disabled = false,
  saving = false
}: AddKVFormProps) {
  const id = useId(), keyId = `${id}-key`, valueId = `${id}-value`;
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [keyError, setKeyError] = useState<string | null>(null);

  useEffect(() => {
    if (clearSignal > 0) {
      setKey("");
      setValue("");
      setKeyError(null);
    }
  }, [clearSignal]);

  const handleKeyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setKey(newValue);
    setKeyError(newValue.trim() ? validateKey(newValue) : null);
  };

  const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disabled || saving) return;

    const validationError = validateKey(key);
    if (validationError) {
      setKeyError(validationError);
      return;
    }

    onAdd(key, value);
  };

  const isKeyInvalid = !!validateKey(key);

  return (
    <form
      className="mb-4 flex flex-col items-start gap-2 lg:flex-row lg:flex-wrap"
      onSubmit={handleSubmit}
    >
      <div className="flex w-full shrink-0 flex-col lg:w-2xs">
        <label className="sr-only" htmlFor={keyId}>
          Key
        </label>
        <input
          id={keyId}
          className={cn(
            "focus-visible:ring-kumo-ring h-9 w-full rounded-md border border-kumo-fill bg-kumo-base px-3 font-mono text-sm text-kumo-default placeholder:text-kumo-subtle focus:border-kumo-brand focus:outline-none focus-visible:ring-2 disabled:bg-kumo-elevated disabled:text-kumo-subtle",
            {
              "border-kumo-danger focus-visible:ring-2 focus-visible:ring-kumo-danger":
                keyError
            }
          )}
          placeholder="Key"
          value={key}
          onChange={handleKeyChange}
          disabled={disabled || saving}
        />
        {keyError && (
          <span className="mt-1 text-xs text-kumo-danger">{keyError}</span>
        )}
      </div>
      <div className="flex w-full flex-1 flex-col lg:min-w-2xs">
        <label className="sr-only" htmlFor={valueId}>
          Value
        </label>
        <textarea
          id={valueId}
          className="focus-visible:ring-kumo-ring max-h-32 min-h-9 w-full resize-none overflow-y-auto rounded-md border border-kumo-fill bg-kumo-base px-3 py-2 font-mono text-sm text-kumo-default placeholder:text-kumo-subtle focus:border-kumo-brand focus:outline-none focus-visible:ring-2 disabled:bg-kumo-elevated disabled:text-kumo-subtle lg:field-sizing-content"
          placeholder="Value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled || saving}
        />
      </div>
      <Button
        type="submit"
        className="w-full shrink-0 lg:w-auto"
        variant="primary"
        disabled={disabled || saving || isKeyInvalid}
        loading={saving}
      >
        {saving ? "Adding..." : "Add entry"}
      </Button>
    </form>
  );
}
